import * as React from "react";
import { $, media, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import MUIAutoComplete from "material-ui/AutoComplete";
import Chip from "material-ui/Chip";
import IconButton from "material-ui/IconButton";
import MenuItem from "material-ui/MenuItem";
import Toggle from "material-ui/Toggle";
import SelectField from "components/form/select-field";
import TextField from "components/form/text-field";
import Close from "components/icons/close";
import { themeOptions } from "styles/theme";
import classNames from "classnames";
import FlatButton from "material-ui/FlatButton";
import { DATATEST } from "constants/integration";
import { CircularProgress } from "material-ui";
import { KEYBOARD_KEYS } from "constants/keyboard";

const AutoComplete: any = MUIAutoComplete;
const CIRCULAR_PROGRESS_SIZE = 28;

export interface IProps {
    filters?: any;
    filterValues?: any;
    groupId: any;
    topLevelFields: any;
    onAddValue: any;
    onRemoveRule: any;
    onRemoveValue: (groupId, ruleId, value) => void;
    onSelectField: any;
    rule: any;
    onFetchFilterValues?: any;
    onResetFilterValues?: () => void;
    onToggleRuleType?: any;
}

export interface IState {
    searchText: string;
}

interface IAutoCompleteRef {
    state: IAutoCompleteState;
}
interface IAutoCompleteState {
    open: boolean;
}

export class Rule extends React.PureComponent<IProps, IState> {
    state = {
        searchText: "",
    };
    autoCompleteRef = React.createRef<IAutoCompleteRef>();
    muiStyles: any = {
        autoComplete: {
            alignSelf: "flex-start",
            border: `1px solid ${themeOptions.palette.borderColor}`,
            borderRadius: "4px",
            display: "inline-block",
            paddingLeft: "10px",
            width: "216px",
        },
        autoCompleteMenuProps: {
            className: "ignoreDeselect",
            maxHeight: 300,
            style: {
                overflowX: "hidden",
            },
            menuItemStyle: {
                whiteSpace: "wrap",
                lineHeight: "24px",
                padding: "8px 0",
                display: "flex",
                alignItems: "center",
            },
            "data-test": DATATEST.CUSTOMER_USER_GROUPS.FILTER_MENU,
        },
        autoCompletePopoverProps: {
            canAutoPosition: true,
            animated: false,
        },
        autoCompleteTextField: {
            width: "100%",
        },
        chip: {
            margin: "0px 8px 8px 0px",
            maxWidth: "-webkit-fill-available",
        },
        chipLabelStyle: {
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        iconButton: {
            margin: "3px 0",
            position: "absolute",
            right: 0,
        },
        toggle: {
            alignSelf: "center",
        },
        toggleIconStyle: {
            alignSelf: "center",
        },
        toggleLabelStyle: {
            alignSelf: "center",
            marginBottom: 0,
        },
        toggleLabelStyleSmall: {
            display: "none",
        },
        selectField: {
            marginRight: "12px",
            minWidth: "164px",
            width: "164px",
        },
        textField: {
            marginRight: "8px",
            minWidth: "216px",
            width: "216px",
        },
    };
    styles = merge(
        {
            borderTop: `1px solid ${themeOptions.palette.lightGrey}`,
            display: "flex",
            flexDirection: "column",
            padding: "16px",
        },
        $(" .top", {
            display: "flex",
            position: "relative",
        }),
        media(
            "(max-width: 900px)",
            $(" .ruleToggle > div > div > label", {
                width: "calc(100% - 100px) !important",
            }),
            $(" .ruleToggle > div > div", {
                justifyContent: "flex-end",
            }),
        ),
        media(
            "(max-width: 700px)",
            $(" .ruleToggle > div > div", {
                justifyContent: "flex-end !important",
            }),
            $(" .ruleToggle > div > div > label", {
                display: "none !important",
            }),
        ),
        media(
            "(max-width: 584px)",
            $(" .top", {
                flexDirection: "column",
            }),
            $(" .top > div", {
                marginBottom: "12px",
            }),
            $(" .ruleToggle", {
                position: "absolute",
            }),
        ),
        $(" .chips", {
            alignSelf: "flex-start",
            display: "flex",
            flexWrap: "wrap",
            width: "100%",
            margin: "16px 0 0",
        }),
        $(" .ruleToggle", {
            alignItems: "center",
            display: "flex",
            flex: 1,
            height: "53px",
            maxWidth: "324px",
            position: "relative",
            right: "60px",
            width: "auto",
        }),
        $(" .textFieldContainer", {
            flex: 1,
            position: "relative",
            display: "flex",
        }),
        $(" .textField > *:first-child", {
            paddingRight: "10px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            width: "100%",
        }),
        $(" .addButton", {
            opacity: 0,
            top: "5px",
            transition: "opacity .3s ease-in-out",
            minWidth: "unset !important",
        }),
        $(" .addButtonVisible", {
            opacity: 1,
        }),
        $(" .addButton span", {
            color: themeOptions.palette.primary1Color,
            padding: "0px !important",
        }),
        $(" .fetchStatus", {
            padding: "4px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "1.2rem",
        }),
    );
    handleNewRequest = (value, index) => {
        const { groupId, onFetchFilterValues, rule } = this.props;
        // * clicking an item will return its index in the list
        if (index !== -1) {
            this.setState({ searchText: "" });
            onFetchFilterValues("add", rule.get("field"), value, { groupId, ruleId: rule.get("_id") });
        }
    };
    handleUpdateInput: any = (searchText, dataSource, eventType) => {
        // * Don't fetch values again after selecting an item from list
        if (eventType.source === "click") {
            this.setState({ searchText: "" });
            return;
        }
        this.setState({ searchText });
        this.props.onFetchFilterValues("fetch", this.props.rule.get("field"), searchText);
    };
    handleFocus = (event: any = {}) => {
        const { id, value } = event.currentTarget;
        const field = id.split("-")[1];
        this.props.onFetchFilterValues("fetch", field, value);
    };
    handleChange = (event, searchText) => {
        this.setState({ searchText });
    };
    handleKeyPress = (event: React.KeyboardEvent) => {
        event.persist();
        if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.TAB) {
            this.props.onAddValue(event);
            this.setState({ searchText: "" });
        }
    };
    handleClose = () => {
        this.props.onResetFilterValues();
    };
    render() {
        const {
            filters,
            filterValues,
            groupId,
            onRemoveRule,
            onRemoveValue,
            onSelectField,
            onFetchFilterValues,
            onToggleRuleType,
            rule,
        } = this.props;
        const { _id, field, type, values } = rule.toObject();
        const whitelistGroupWarning = field && values.size === 0;
        let topLevelFields = this.props.topLevelFields;
        let invalidTopLevelField = false;
        if (!topLevelFields.has(field)) {
            // ! This top-level field was deleted/made unfilterable
            // ! but a group still exists with this field as a rule
            invalidTopLevelField = true;
            topLevelFields = <MenuItem primaryText={field} value={field} />;
        } else {
            topLevelFields = topLevelFields
                .sortBy((topLevelField) => topLevelField.get("name"))
                .reduce((accumulator, topLevelField) => {
                    return accumulator.concat(
                        <MenuItem
                            data-selectrulefield={`${groupId}-${_id}-${topLevelField.get("key")}`}
                            key={`${groupId}-${_id}-${topLevelField.get("key")}`}
                            primaryText={topLevelField.get("name")}
                            data-test={DATATEST.CUSTOMER_USER_GROUPS.RULE_FIELD}
                            onClick={onSelectField}
                            value={topLevelField.get("key")}
                        />,
                    );
                }, []);
        }
        return (
            <div
                data-test={DATATEST.CUSTOMER_USER_GROUPS.GROUP_RULE}
                className="rule"
                data-testid={`${groupId}-${_id}-rule`}
                {...this.styles}
            >
                <div className="top">
                    <SelectField
                        data-selectrule={`${groupId}-${_id}`}
                        data-test={DATATEST.CUSTOMER_USER_GROUPS.RULE_FIELD_SELECT}
                        disabled={values.size > 0}
                        large={true}
                        name="select"
                        value={field}
                        style={this.muiStyles.selectField}
                        {...(invalidTopLevelField && {
                            errorText: formatMessage(
                                "This field is no longer valid for creating new rules and can be removed.",
                            ),
                            labelClassName: "requiredFieldError",
                            required: true,
                        })}
                    >
                        {topLevelFields}
                    </SelectField>
                    <div className="textFieldContainer">
                        {onFetchFilterValues && filters && filterValues && !rule.has("hide") && (
                            <>
                                <AutoComplete
                                    ref={this.autoCompleteRef}
                                    className="textField"
                                    dataSource={filterValues}
                                    data-addvalue={`${groupId}-${_id}`}
                                    filter={AutoComplete.noFilter}
                                    fullWidth={false}
                                    hintText={formatMessage("Enter a |field| value", { field })}
                                    id={`${_id}-${field}`}
                                    menuCloseDelay={0}
                                    menuProps={this.muiStyles.autoCompleteMenuProps}
                                    onClose={this.handleClose}
                                    onFocus={this.handleFocus}
                                    onNewRequest={this.handleNewRequest}
                                    onUpdateInput={this.handleUpdateInput}
                                    openOnFocus={!filters.isFetching}
                                    popoverProps={this.muiStyles.autoCompletePopoverProps}
                                    searchText={this.state.searchText}
                                    style={this.muiStyles.autoComplete}
                                    textFieldStyle={this.muiStyles.autoCompleteTextField}
                                    underlineShow={false}
                                    data-test={DATATEST.CUSTOMER_USER_GROUPS.RULE_TEXT_INPUT}
                                />
                                {this.autoCompleteRef.current?.state?.open && (
                                    <div className="fetchStatus">
                                        {filters.isFetching && <CircularProgress size={CIRCULAR_PROGRESS_SIZE} />}
                                        {!filters.isFetching &&
                                            !filterValues.length &&
                                            formatMessage("No results found")}
                                    </div>
                                )}
                            </>
                        )}
                        {!onFetchFilterValues && (
                            <React.Fragment>
                                <TextField
                                    className={classNames({
                                        textField: true,
                                        hidden: !!rule.get("hide"),
                                    })}
                                    disabled={invalidTopLevelField}
                                    data-addvalue={`${groupId}-${_id}`}
                                    key={`${_id}-${field}`}
                                    name="input"
                                    dataTest={DATATEST.CUSTOMER_USER_GROUPS.RULE_TEXT_INPUT}
                                    onChange={this.handleChange}
                                    onKeyPress={this.handleKeyPress}
                                    style={this.muiStyles.textField}
                                    value={this.state.searchText}
                                    {...(field && {
                                        placeholder: formatMessage("Enter a new |field|", { field }),
                                    })}
                                    {...(whitelistGroupWarning && {
                                        errorText: formatMessage("Rules need at least one value"),
                                        labelClassName: "requiredFieldError",
                                        required: true,
                                    })}
                                />
                                <FlatButton
                                    className={classNames({
                                        addButton: true,
                                        addButtonVisible: this.state.searchText !== "",
                                    })}
                                    disableTouchRipple={true}
                                    disabled={true}
                                    label={"↵ " + formatMessage("Enter")}
                                    data-test={DATATEST.CUSTOMER_USER_GROUPS.ENTER_RULE_VALUE_ICON}
                                    hoverColor="rgba(0,0,0,0)"
                                />
                            </React.Fragment>
                        )}
                    </div>
                    <div
                        className={classNames({
                            hidden: !onToggleRuleType,
                            hideLabel: !rule.has("hide"),
                            ruleToggle: true,
                        })}
                    >
                        {onToggleRuleType && (
                            <Toggle
                                iconStyle={this.muiStyles.toggleIconStyle}
                                data-ruletoggle={`${groupId}-${_id}`}
                                label={formatMessage("Display all products with |field| values", { field })}
                                labelPosition="left"
                                onToggle={onToggleRuleType}
                                toggled={rule.has("hide")}
                                value={type}
                                labelStyle={this.muiStyles.toggleLabelStyle}
                                style={this.muiStyles.toggle}
                            />
                        )}
                    </div>
                    <IconButton
                        className="remove"
                        data-removerule={`${groupId}-${_id}`}
                        data-test={DATATEST.CUSTOMER_USER_GROUPS.REMOVE_RULE_BTN}
                        onClick={onRemoveRule}
                        style={this.muiStyles.iconButton}
                    >
                        <Close />
                    </IconButton>
                </div>
                {values.size !== 0 && (
                    <div className="chips">
                        {!rule.get("hide") &&
                            values.reduce((reducer, value) => {
                                if (value !== "*") {
                                    reducer = reducer.concat(
                                        <Chip
                                            data-removevalue={`${groupId}-${_id}-${value}`}
                                            key={`${groupId}-${_id}-${value}`}
                                            data-test={DATATEST.CUSTOMER_USER_GROUPS.RULE_CHIP}
                                            onRequestDelete={onRemoveValue.bind(this, groupId, _id, value)}
                                            style={this.muiStyles.chip}
                                            labelStyle={this.muiStyles.chipLabelStyle}
                                        >
                                            {value}
                                        </Chip>,
                                    );
                                }
                                return reducer;
                            }, [])}
                    </div>
                )}
            </div>
        );
    }
}

export default Rule;
